'use client'

import * as React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from 'recharts'
import { collection, getDocs } from 'firebase/firestore'
import { db } from '../../../../firebase'

const parseAmount = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const sanitized = value.replace(/[^0-9.-]+/g, '')
    const parsed = Number(sanitized)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

export default function RiskIssuesDashboard() {
  const [risks, setRisks] = React.useState([])
  const [issues, setIssues] = React.useState([])
  const [projects, setProjects] = React.useState([])
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    let isMounted = true

    async function loadRiskIssues() {
      try {
        const [riskSnap, issueSnap, projectSnap] = await Promise.all([
          getDocs(collection(db, 'risks')),
          getDocs(collection(db, 'issues')),
          getDocs(collection(db, 'projects')),
        ])
        if (!isMounted) return
        setRisks(riskSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })))
        setIssues(issueSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })))
        setProjects(projectSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })))
      } catch {
        if (!isMounted) return
        setRisks([])
        setIssues([])
        setProjects([])
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    loadRiskIssues()

    return () => {
      isMounted = false
    }
  }, [])

  const normalizedRisks = risks.map((risk) => ({
    ...risk,
    status: String(risk.status || 'open').toLowerCase(),
    category: risk.category || 'Other',
    riskScore: parseAmount(risk.riskScore || 0),
  }))

  const normalizedIssues = issues.map((issue) => ({
    ...issue,
    status: String(issue.status || 'open').toLowerCase(),
  }))

  const totalRisks = normalizedRisks.length
  const openRisks = normalizedRisks.filter((risk) => risk.status === 'open').length
  const criticalRisks = normalizedRisks.filter((risk) => risk.riskScore >= 15).length
  const mitigatedRisks = normalizedRisks.filter((risk) => risk.status === 'mitigated').length

  const totalIssues = normalizedIssues.length
  const openIssues = normalizedIssues.filter((issue) => issue.status === 'open').length
  const resolvedIssues = normalizedIssues.filter((issue) => issue.status === 'resolved' || issue.status === 'closed').length

  const resolvedTotal = mitigatedRisks + resolvedIssues

  const riskStatusPie = [
    { name: 'Open', value: openRisks, color: 'oklch(0.577 0.245 27.325)' },
    { name: 'Mitigated', value: mitigatedRisks, color: 'oklch(0.55 0.16 150)' },
    { name: 'Closed', value: normalizedRisks.filter((risk) => risk.status === 'closed').length, color: 'oklch(0.87 0.15 85)' },
  ]

  const issueStatusPie = [
    { name: 'Open', value: openIssues, color: 'oklch(0.577 0.245 27.325)' },
    { name: 'In Progress', value: normalizedIssues.filter((issue) => issue.status === 'in-progress').length, color: 'oklch(0.35 0.12 145)' },
    { name: 'Resolved', value: normalizedIssues.filter((issue) => issue.status === 'resolved').length, color: 'oklch(0.55 0.16 150)' },
    { name: 'Closed', value: normalizedIssues.filter((issue) => issue.status === 'closed').length, color: 'oklch(0.87 0.15 85)' },
  ]

  const riskByProject = projects.map((project) => ({
    project: project.code || project.shortCode || project.title || 'Project',
    risks: normalizedRisks.filter((risk) => risk.projectId === project.id).length,
    issues: normalizedIssues.filter((issue) => issue.projectId === project.id).length,
  }))

  const riskCategoryCounts = normalizedRisks.reduce((acc, risk) => {
    acc[risk.category] = (acc[risk.category] || 0) + 1
    return acc
  }, {})
  const riskCategoryData = Object.entries(riskCategoryCounts).map(([name, count]) => ({ name, count }))

  const radarData = projects.map((project) => {
    const risksForProject = normalizedRisks.filter((risk) => risk.projectId === project.id)
    const avgScore = risksForProject.length
      ? Math.round(risksForProject.reduce((sum, risk) => sum + parseAmount(risk.riskScore || 0), 0) / risksForProject.length)
      : 0
    return { project: project.code || project.shortCode || project.title || 'Project', riskScore: avgScore }
  })

  return (
    <div className="flex flex-col gap-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground font-serif tracking-tight">Risk & Issues Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Combined view of portfolio risks and project issues</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-foreground">{loading ? '—' : totalRisks}</p>
            <p className="text-[10px] text-muted-foreground font-medium mt-1">Total Risks</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-destructive" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-destructive">{loading ? '—' : openRisks}</p>
            <p className="text-[10px] text-muted-foreground font-medium mt-1">Open Risks</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-destructive/50" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-foreground">{loading ? '—' : criticalRisks}</p>
            <p className="text-[10px] text-muted-foreground font-medium mt-1">Critical</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-destructive" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-foreground">{loading ? '—' : totalIssues}</p>
            <p className="text-[10px] text-muted-foreground font-medium mt-1">Total Issues</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-warning" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-warning">{loading ? '—' : openIssues}</p>
            <p className="text-[10px] text-muted-foreground font-medium mt-1">Open Issues</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-warning/50" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-success">{loading ? '—' : resolvedTotal}</p>
            <p className="text-[10px] text-muted-foreground font-medium mt-1">Resolved</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-success" />
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="border border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-foreground">Risk Status</CardTitle></CardHeader>
          <CardContent className="flex flex-col items-center">
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie
                  data={riskStatusPie}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={60}
                  dataKey="value"
                  paddingAngle={4}
                  strokeWidth={0}
                >
                  {riskStatusPie.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex items-center gap-3">
              {riskStatusPie.map((entry) => (
                <div key={entry.name} className="flex items-center gap-1.5">
                  <span className="size-2 rounded-full" style={{ backgroundColor: entry.color }} />
                  <span className="text-[10px] text-muted-foreground">
                    {entry.name} ({entry.value})
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        <Card className="border border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-foreground">Issue Status</CardTitle></CardHeader>
          <CardContent className="flex flex-col items-center">
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie
                  data={issueStatusPie}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={60}
                  dataKey="value"
                  paddingAngle={4}
                  strokeWidth={0}
                >
                  {issueStatusPie.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {issueStatusPie.map((entry) => (
                <div key={entry.name} className="flex items-center gap-1.5">
                  <span className="size-2 rounded-full" style={{ backgroundColor: entry.color }} />
                  <span className="text-[10px] text-muted-foreground">
                    {entry.name} ({entry.value})
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        <Card className="border border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-foreground">Avg Risk Score by Project</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="oklch(0.9 0.01 145)" />
                <PolarAngleAxis dataKey="project" tick={{ fontSize: 9, fill: 'oklch(0.48 0.03 145)' }} />
                <PolarRadiusAxis tick={{ fontSize: 8 }} domain={[0, 20]} />
                <Radar dataKey="riskScore" stroke="oklch(0.577 0.245 27.325)" fill="oklch(0.577 0.245 27.325)" fillOpacity={0.3} />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
              </RadarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="border border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-foreground">Risks & Issues by Project</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={riskByProject} barSize={16}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.9 0.01 145)" />
                <XAxis dataKey="project" tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                <YAxis tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="risks" fill="oklch(0.577 0.245 27.325)" name="Risks" radius={[4, 4, 0, 0]} />
                <Bar dataKey="issues" fill="oklch(0.87 0.15 85)" name="Issues" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card className="border border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-foreground">Risk Categories</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={riskCategoryData} layout="vertical" barSize={16}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.9 0.01 145)" />
                <XAxis type="number" tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 9 }} stroke="oklch(0.48 0.03 145)" width={90} />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                <Bar dataKey="count" fill="oklch(0.35 0.12 145)" radius={[0, 4, 4, 0]} label={{ position: 'right', fontSize: 10, fill: 'oklch(0.48 0.03 145)' }} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
